import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

function SignupPage() {
  const { isDarkMode, toggleTheme, themeClasses } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Registration Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Extract invite code from URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inviteParam = params.get('invite') || params.get('token') || params.get('code');

    if (inviteParam) {
      setFormData((prev) => ({
        ...prev,
        inviteCode: inviteParam,
      }));
    }
  }, [location.search]);

  // --- LOCAL LANGUAGE STATE ---
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'english';
  });
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', newLanguage);
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    
    window.dispatchEvent(new Event('languageChange'));
  };

  // --- TRANSLATIONS ---
  const translations = {
    english: {
      'signup.title': 'Create Account',
      'signup.inviteCode': 'Invitation Code',
      'signup.inviteCodeHelp': 'Contact an administrator to request access.',
      'signup.name': 'Full Name',
      'signup.email': 'Email Address',
      'signup.password': 'Password',
      'signup.confirmPassword': 'Confirm Password',
      'signup.submit': 'Register',
      'signup.submitting': 'Creating account...',
      'signup.haveAccount': 'Already have an account?',
      'signup.login': 'Sign In',
      'signup.error.passwordMatch': 'Passwords do not match',
      'signup.error.inviteRequired': 'An invitation code is required to register.',
      'signup.error.generic': 'Registration failed. Please try again.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'signup.title': 'צור חשבון',
      'signup.inviteCode': 'קוד הזמנה',
      'signup.inviteCodeHelp': 'צור קשר עם מנהל מערכת לקבלת גישה.',
      'signup.name': 'שם מלא',
      'signup.email': 'כתובת אימייל',
      'signup.password': 'סיסמה',
      'signup.confirmPassword': 'אימות סיסמה',
      'signup.submit': 'הרשם',
      'signup.submitting': 'יוצר חשבון...',
      'signup.haveAccount': 'כבר יש לך חשבון?',
      'signup.login': 'התחבר',
      'signup.error.passwordMatch': 'הסיסמאות אינן תואמות',
      'signup.error.inviteRequired': 'קוד הזמנה נדרש להרשמה.',
      'signup.error.generic': 'ההרשמה נכשלה. אנא נסה שוב.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    }
  };
  const t = (key) => translations[language]?.[key] || key;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password !== formData.confirmPassword) {
      return setError(t('signup.error.passwordMatch'));
    }

    if (!formData.inviteCode.trim()) {
      return setError(t('signup.error.inviteRequired'));
    }

    setError('');
    setIsLoading(true);

    try {
      // Base API URL handling depending on your environment setup
      // If you use a proxy in package.json or vite.config.js, '/api/auth/signup' works perfectly.
      const apiUrl = import.meta.env.VITE_API_URL || process.env.REACT_APP_API_URL || 'https://newdietitianweb-615263253386.europe-west3.run.app';
      
      // Send the user back to /auth/confirm on this same origin after they
      // click the confirmation link. This must be a concrete URL — Supabase's
      // Site URL (the fallback) MUST NOT be a wildcard pattern, otherwise the
      // browser can't navigate to the redirect target.
      const emailRedirectTo = (typeof window !== 'undefined' && window.location?.origin)
        ? `${window.location.origin}/auth/confirm`
        : undefined;

      const response = await fetch(`${apiUrl}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          inviteCode: formData.inviteCode,
          emailRedirectTo,
        }),
      });

      const contentType = response.headers.get('content-type');
      let data = null;
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text && text.trim()) {
          try {
            data = JSON.parse(text);
          } catch (_) {
            data = null;
          }
        }
      }

      if (!response.ok) {
        if (response.status === 405) {
          throw new Error('Registration endpoint not available. Make sure the API server is running and VITE_API_URL points to it.');
        }
        throw new Error(data?.error || `Registration failed (${response.status}).`);
      }

      if (!data) {
        throw new Error(t('signup.error.generic'));
      }
      
      // Navigate to home (login page at /) and show confirm-email message
      navigate('/', { state: { fromSignup: true } });
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || t('signup.error.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = `w-full px-4 py-3 ${isDarkMode ? 'bg-moss/50 border-line text-white' : 'bg-canvas border-line text-ink'} border rounded-lg placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition-all`;
  const labelClasses = `block text-sm font-medium ${themeClasses.textSecondary} mb-2`;

  return (
    <div 
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12 transition-colors duration-300`}
      dir={direction}
    >
      {/* Language and Theme Toggle Buttons - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={toggleTheme}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'}`}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>

        <button
          onClick={toggleLanguage}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} ${isDarkMode ? 'text-forest' : 'text-forest-ondark'} p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'} font-semibold`}
          aria-label={language === 'english' ? 'Switch to Hebrew' : 'Switch to English'}
        >
          {language === 'english' ? 'ע' : 'EN'}
        </button>
      </div>

      <div className="w-full max-w-md">
        {/* Logo/Brand Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img 
              src="/logo.png" 
              alt="BetterChoice Logo" 
              className="w-full h-full object-contain" 
            />
          </div>
          <h1 className={`text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>
            {t('brand.name')}
          </h1>
          <p className={`${isDarkMode ? 'text-forest' : 'text-forest-ondark'} text-lg font-medium`}>
            {t('brand.portal')}
          </p>
        </div>

        {/* Signup Card */}
        <div className={`${themeClasses.bgCard}  rounded-lg ${themeClasses.shadowCard} p-8 border ${themeClasses.borderPrimary}`}>
          <h2 className={`text-2xl font-semibold ${themeClasses.textPrimary} mb-6 text-center`}>
            {t('signup.title')}
          </h2>

          {error && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-100 border-red-300 text-red-700'} border rounded-lg text-sm`}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Invite Code Input */}
            <div>
              <label htmlFor="inviteCode" className={labelClasses}>
                {t('signup.inviteCode')}
              </label>
              <input
                id="inviteCode"
                name="inviteCode"
                type="text"
                value={formData.inviteCode}
                onChange={handleChange}
                required
                className={inputClasses}
              />
              <span className={`text-xs mt-1 block ${themeClasses.textMuted}`}>
                {t('signup.inviteCodeHelp')}
              </span>
            </div>

            {/* Full Name Input */}
            <div>
              <label htmlFor="name" className={labelClasses}>
                {t('signup.name')}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                className={inputClasses}
              />
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="email" className={labelClasses}>
                {t('signup.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={inputClasses}
              />
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className={labelClasses}>
                {t('signup.password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                className={inputClasses}
              />
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className={labelClasses}>
                {t('signup.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className={inputClasses}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full mt-2 ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300   transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('signup.submitting')}
                </span>
              ) : (
                t('signup.submit')
              )}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center text-sm">
            <span className={themeClasses.textSecondary}>{t('signup.haveAccount')} </span>
            <button 
              onClick={() => navigate('/')}
              className={`font-semibold ${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors`}
            >
              {t('signup.login')}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default SignupPage;